import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ErrorAuth, solicitarCodigoSms, verificarCodigoSms } from '@/lib/auth';
import { CircleNotch } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

const CODIGO_LARGO = 6;
const REENVIO_SEGUNDOS = 30;

interface Props {
  onExito: () => void;
  /** Cambia a la pantalla de usuario + PIN (respaldo si el SMS no llega o falla). */
  onUsarPin: () => void;
}

/** Login por código SMS: teléfono → código de 6 dígitos. */
export function LoginSms({ onExito, onUsarPin }: Props) {
  const [telefono, setTelefono] = useState('');
  const [codigo, setCodigo] = useState('');
  const [paso, setPaso] = useState<'telefono' | 'codigo'>('telefono');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [espera, setEspera] = useState(0);
  const codigoRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (espera <= 0) return;
    const t = setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [espera]);

  // Auto-submit al completar los 6 dígitos (solo cuando cambia el código).
  // biome-ignore lint/correctness/useExhaustiveDependencies: intencional, solo en cambio de código
  useEffect(() => {
    if (paso === 'codigo' && codigo.length === CODIGO_LARGO && !cargando) void verificar();
  }, [codigo]);

  async function pedirCodigo() {
    if (cargando) return;
    if (telefono.replace(/\D/g, '').length < 8) {
      setError('Ingresá tu número de teléfono (8 dígitos)');
      return;
    }
    setError(null);
    setCargando(true);
    try {
      await solicitarCodigoSms(telefono);
      setPaso('codigo');
      setCodigo('');
      setEspera(REENVIO_SEGUNDOS);
      setTimeout(() => codigoRef.current?.focus(), 50);
    } catch (e) {
      setError(e instanceof ErrorAuth ? e.message : 'No se pudo enviar el código');
    } finally {
      setCargando(false);
    }
  }

  async function verificar() {
    if (cargando) return;
    setError(null);
    setCargando(true);
    try {
      const u = await verificarCodigoSms(telefono, codigo);
      toast.success(`Bienvenido, ${u.nombre}`);
      onExito();
    } catch (e) {
      setError(e instanceof ErrorAuth ? e.message : 'Error al verificar el código');
      setCodigo('');
    } finally {
      setCargando(false);
    }
  }

  return (
    <div className="space-y-5">
      {paso === 'telefono' ? (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="telefono" className="text-xs font-medium">
              Número de teléfono
            </Label>
            <Input
              id="telefono"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="8888-1234"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void pedirCodigo()}
              disabled={cargando}
              className="h-12 text-lg"
            />
            <p className="text-xs text-muted-foreground">
              Te enviaremos un código por mensaje de texto.
            </p>
          </div>
          <Button
            className="w-full"
            size="lg"
            onClick={pedirCodigo}
            disabled={cargando}
            type="button"
          >
            {cargando ? (
              <>
                <CircleNotch
                  className="mr-2 h-4 w-4 animate-spin"
                  weight="bold"
                  aria-hidden="true"
                />
                Enviando…
              </>
            ) : (
              'Enviar código'
            )}
          </Button>
        </>
      ) : (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="codigo" className="text-xs font-medium">
              Código de 6 dígitos
            </Label>
            <Input
              id="codigo"
              ref={codigoRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={CODIGO_LARGO}
              placeholder="······"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, CODIGO_LARGO))}
              disabled={cargando}
              className="h-14 text-center font-mono text-2xl tracking-[0.5em]"
            />
            <p className="text-xs text-muted-foreground">
              Si el número está registrado, el código llega en unos segundos.
            </p>
          </div>
          <Button
            className="w-full"
            size="lg"
            onClick={verificar}
            disabled={cargando || codigo.length !== CODIGO_LARGO}
            type="button"
          >
            {cargando ? (
              <>
                <CircleNotch
                  className="mr-2 h-4 w-4 animate-spin"
                  weight="bold"
                  aria-hidden="true"
                />
                Verificando…
              </>
            ) : (
              'Ingresar'
            )}
          </Button>
          <div className="flex items-center justify-between text-xs">
            <button
              type="button"
              className="text-muted-foreground underline"
              onClick={() => {
                setPaso('telefono');
                setError(null);
              }}
            >
              Cambiar número
            </button>
            <button
              type="button"
              className="text-adelante-primary underline disabled:text-muted-foreground disabled:no-underline"
              onClick={pedirCodigo}
              disabled={espera > 0 || cargando}
            >
              {espera > 0 ? `Reenviar en ${espera}s` : 'Reenviar código'}
            </button>
          </div>
        </>
      )}

      {error && (
        <p className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-center text-xs text-destructive">
          {error}
        </p>
      )}

      {(error || paso === 'codigo') && (
        <Button
          variant="outline"
          className="w-full"
          onClick={onUsarPin}
          disabled={cargando}
          type="button"
        >
          {error ? 'Entrar con usuario y PIN' : '¿No te llegó el código? Entrar con PIN'}
        </Button>
      )}
    </div>
  );
}
